import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { TeamPresenter } from '@/components/team/team.presenter';
import { toast } from '@/components/ui/sonner';
import type { MemberDto } from '@/generated/korza-api-client/types.gen';
import { useSelf } from '@/hooks/use-self';
import {
  TeamMemberErrorKind,
  TeamMemberRequestError,
  useInviteMember,
  useRemoveMember,
  useTenantMembers,
  useUpdateOwnName,
} from '@/hooks/use-tenant-members';
import { useI18nContext } from '@/i18n/i18n-react';
import { SidebarLayout } from '@/layouts/sidebar.layout';

export default function TeamPage() {
  const { LL } = useI18nContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const self = useSelf();
  const membersQuery = useTenantMembers();
  const inviteMember = useInviteMember();
  const removeMember = useRemoveMember();
  const updateOwnName = useUpdateOwnName();

  const messageFor = (kind: TeamMemberErrorKind): string => {
    switch (kind) {
      case 'rate-limited':
        return LL.team.errors.rateLimited();
      case 'last-member':
        return LL.team.errors.lastMember();
      case 'not-found':
        return LL.team.errors.notFound();
      default:
        return LL.team.errors.generic();
    }
  };

  const handleInvite = async (email: string): Promise<boolean> => {
    try {
      await inviteMember.mutateAsync({ email });
      toast.success(LL.team.invite.success({ email }));
      return true;
    } catch {
      return false;
    }
  };

  const handleRenameSelf = async (name: string): Promise<boolean> => {
    try {
      await updateOwnName.mutateAsync({ name });
    } catch (error) {
      toast.error(
        messageFor(
          error instanceof TeamMemberRequestError ? error.kind : 'unknown'
        )
      );
      return false;
    }

    toast.success(LL.team.editName.success());
    return true;
  };

  const handleRemove = async (member: MemberDto): Promise<void> => {
    try {
      await removeMember.mutateAsync({ adminUserId: member.adminUserId });
    } catch (error) {
      toast.error(
        messageFor(
          error instanceof TeamMemberRequestError ? error.kind : 'unknown'
        )
      );
      return;
    }

    toast.success(LL.team.remove.success());

    if (member.adminUserId === self.id) {
      // The store list must not still offer the store we just left.
      await queryClient.invalidateQueries({ queryKey: ['enrolled-tenants'] });
      navigate('/select-tenant');
    }
  };

  const status = membersQuery.isPending
    ? 'loading'
    : membersQuery.isError
      ? 'error'
      : 'ready';

  return (
    <SidebarLayout>
      <TeamPresenter
        status={status}
        members={membersQuery.data ?? []}
        selfId={self.id}
        onRetry={() => void membersQuery.refetch()}
        onInvite={handleInvite}
        isInviting={inviteMember.isPending}
        inviteErrorMessage={
          inviteMember.error ? messageFor(inviteMember.error.kind) : null
        }
        onRenameSelf={handleRenameSelf}
        isRenamingSelf={updateOwnName.isPending}
        onRemove={handleRemove}
        isRemoving={removeMember.isPending}
      />
    </SidebarLayout>
  );
}
